// LSP-17773: the per-recipient claim token is a credential-equivalent single-use secret and rides
// in the URL path (/share/:token, and the /share-claims/:token API calls). Telemetry (Datadog Logs
// and RUM) and console error logging ingest URLs, referrers, and messages, so redact the token —
// and the autologin hand-off's credential query params — before any of them leaves the browser.
// mask-user-input only masks input VALUES, never URLs, so this scrubber is the actual defense.
// Shared by src/plugins/datadog.ts (beforeSend hooks) and src/api/index.ts (HTTP error logging)
// so there is exactly one redaction implementation.
// The negated classes below all exclude whitespace (\s) as well as URL delimiters (Bugbot
// follow-up): this scrubber also runs over MULTI-LINE error.stack values, and a class like
// [^/?#]+ happily consumes newlines — collapsing the rest of a token-bearing stack frame
// plus the head of the next frame into one [REDACTED], destroying the function names and
// line numbers those events exist to carry. A URL never contains literal whitespace, so
// stopping at it never under-redacts.
export function scrubClaimToken(url: string | undefined): string | undefined {
	if (!url) return url;
	return (
		url
			// Literal path: /share/<token>, /share-claims/<token>.
			.replace(/(\/share(?:-claims)?\/)[^/?#\s]+/gi, "$1[REDACTED]")
			// Percent-encoded (code-review #A): the signup hand-off routes through
			// /autologin/?returnPath=%2Fshare%2F<token>, which becomes view.referrer on the
			// live claim page while the single-use token is still claimable. base64url tokens
			// carry only unreserved chars, so the encoded segment runs to the next &/#.
			.replace(/(%2[Ff]share(?:-claims)?%2[Ff])[^&#\s]*/g, "$1[REDACTED]")
			// Autologin hand-off credentials: ?accessToken=…&idToken=…&refreshToken=…&secret=…
			// are full Auth0 credentials plus the shared autologin secret — redact their values
			// wherever a URL carrying them is logged.
			.replace(/([?&](?:accessToken|idToken|refreshToken|secret)=)[^&#\s]*/gi, "$1[REDACTED]")
	);
}
